import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, Clock, Plus, Trash2, ChevronDown, DollarSign } from "lucide-react";
import { api } from "../lib/api";

const STATUS_OPTIONS = ["new", "reviewing", "in_progress", "completed", "rejected"];
const STATUS_COLORS = {
  new: "bg-[#00D4FF]/10 text-[#00D4FF] border-[#00D4FF]/25",
  reviewing: "bg-[#8B2FE8]/10 text-[#8B2FE8] border-[#8B2FE8]/25",
  in_progress: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  completed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  rejected: "bg-red-500/10 text-red-600 border-red-500/20",
};

export default function AdminQuoteCard({ quote: q, index, onStatusChange, onProgressUpdated }) {
  const [open, setOpen] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [percent, setPercent] = useState(25);
  const [submitting, setSubmitting] = useState(false);
  const [milestoneError, setMilestoneError] = useState("");

  const loadMilestones = useCallback(async () => {
    try {
      const { data } = await api.get(`/quotes/${q.id}/tracking`);
      setMilestones(data.milestones);
      setLoaded(true);
    } catch (e) {
      setMilestoneError("Failed to load milestones.");
      console.error("Milestone load error:", e);
    }
  }, [q.id]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) await loadMilestones();
  };

  const addMilestone = async () => {
    if (!title || title.length < 2) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/quotes/${q.id}/milestones`, { title, note, percent: Number(percent) });
      setTitle(""); setNote("");
      await loadMilestones();
      onProgressUpdated && onProgressUpdated();
    } catch (e) {
      console.error("Add milestone error:", e);
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/milestones/${id}`);
    setMilestones((ms) => ms.filter((m) => m.id !== id));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      data-testid={`admin-quote-${q.id}`}
      className="glass-card rounded-2xl p-6 border border-black/[0.06] hover:border-[#00D4FF]/20 transition"
    >
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#00D4FF]">{q.service_type}</p>
          <h3 className="font-display font-bold text-xl mt-1">{q.project_title}</h3>
        </div>
        <select
          value={q.status}
          onChange={(e) => onStatusChange(q.id, e.target.value)}
          data-testid={`admin-status-select-${q.id}`}
          className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-[0.2em] border outline-none cursor-pointer ${STATUS_COLORS[q.status] || STATUS_COLORS.new}`}
        >
          {STATUS_OPTIONS.map((s) => <option key={s} value={s} className="bg-white">{s.replace("_", " ")}</option>)}
        </select>
      </div>
      <p className="text-sm text-[#4B5563] leading-relaxed whitespace-pre-wrap">{q.description}</p>

      {/* progress bar */}
      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-cyan-400 to-teal-400" style={{ width: `${q.progress || 0}%` }} />
        </div>
        <span className="text-xs font-mono text-[#00D4FF]">{q.progress || 0}%</span>
      </div>

      <div className="mt-5 pt-5 border-t border-black/[0.06] grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs text-[#5A5A72]">
        <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#00D4FF]" /><a href={`mailto:${q.contact_email}`} className="hover:text-white">{q.contact_email}</a></div>
        {q.contact_phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#00D4FF]" />{q.contact_phone}</div>}
        {q.budget_range && <div className="flex items-center gap-2"><DollarSign className="w-3.5 h-3.5 text-[#00D4FF]" />{q.budget_range}</div>}
        {q.timeline && <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-[#00D4FF]" />{q.timeline}</div>}
      </div>

      <div className="mt-3 flex justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-[#6B7280]">
        <span>{q.contact_name}</span>
        <span>{new Date(q.created_at).toLocaleString()}</span>
      </div>

      <button
        onClick={toggle}
        data-testid={`admin-toggle-milestones-${q.id}`}
        className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#00D4FF] hover:text-[#00D4FF]"
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        {open ? "Hide" : "Manage"} Milestones
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mt-4 overflow-hidden"
          >
            <div className="rounded-xl border border-black/[0.06] bg-white p-5">
              {milestoneError && (
                <p className="text-red-400 text-xs mb-3">{milestoneError}</p>
              )}
              {/* Add new milestone */}
              <div className="grid sm:grid-cols-12 gap-3">
                <input
                  value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Milestone title (e.g. Wireframes done)"
                  data-testid={`admin-milestone-title-${q.id}`}
                  className="sm:col-span-5 px-3 py-2.5 rounded-lg bg-[#F8F8F6] border border-black/[0.08] text-sm focus:border-[#00D4FF]/60 outline-none"
                />
                <input
                  value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note for the customer"
                  data-testid={`admin-milestone-note-${q.id}`}
                  className="sm:col-span-5 px-3 py-2.5 rounded-lg bg-[#F8F8F6] border border-black/[0.08] text-sm focus:border-[#00D4FF]/60 outline-none"
                />
                <div className="sm:col-span-2 flex gap-2">
                  <input
                    type="number" min={0} max={100} value={percent} onChange={(e) => setPercent(e.target.value)}
                    data-testid={`admin-milestone-percent-${q.id}`}
                    className="w-full px-3 py-2.5 rounded-lg bg-[#F8F8F6] border border-black/[0.08] text-sm text-[#00D4FF] focus:border-[#00D4FF]/60 outline-none"
                  />
                </div>
                <button
                  onClick={addMilestone} disabled={submitting || title.length < 2}
                  data-testid={`admin-milestone-add-${q.id}`}
                  className="sm:col-span-12 btn-primary px-4 py-2.5 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-40"
                >
                  <Plus className="w-4 h-4" /> {submitting ? "Adding..." : "Add Milestone"}
                </button>
              </div>

              {/* List */}
              <div className="mt-5 space-y-3">
                {milestones.length === 0 ? (
                  <p className="text-xs text-[#6B7280] font-mono uppercase tracking-[0.2em] text-center py-4">No milestones yet</p>
                ) : (
                  milestones.map((m) => (
                    <div key={m.id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-[#F8F8F6] border border-black/[0.06]">
                      <div className="w-2 h-2 rounded-full bg-[#00D4FF] glow-cyan mt-2" />
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="font-display font-semibold text-sm">{m.title}</p>
                          <span className="text-xs font-mono text-[#00D4FF]">{m.percent}%</span>
                        </div>
                        {m.note && <p className="text-xs text-[#5A5A72] mt-1">{m.note}</p>}
                        <p className="text-[10px] font-mono text-[#6B7280] mt-1">{new Date(m.created_at).toLocaleString()}</p>
                      </div>
                      <button
                        onClick={() => remove(m.id)} data-testid={`admin-milestone-delete-${m.id}`}
                        className="text-[#6B7280] hover:text-red-400 transition"
                        aria-label="Delete milestone"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
